/** Async key/value store mirroring the persisted stores (VOL-14). */
export interface AsyncKeyValueStore {
  get(key: string): Promise<string | null>;
  /** Applies every entry in one transaction; `null` deletes the key. */
  write(entries: ReadonlyArray<readonly [string, string | null]>): Promise<void>;
}

const DB_NAME = 'volleystats-durable';
const STORE_NAME = 'kv';

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/** Null where IndexedDB does not exist (old browser, tests): callers keep localStorage only. */
export function createIndexedDbStore(
  factory: IDBFactory | undefined = typeof indexedDB === 'undefined' ? undefined : indexedDB,
): AsyncKeyValueStore | null {
  if (!factory) return null;
  let dbPromise: Promise<IDBDatabase> | null = null;

  const openVersion = (version: number | undefined, repair: boolean): Promise<IDBDatabase> =>
    new Promise<IDBDatabase>((resolve, reject) => {
      let blocked = false;
      const request = version === undefined ? factory.open(DB_NAME) : factory.open(DB_NAME, version);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        // Opened after it was given up as blocked: nobody holds this connection.
        if (blocked) {
          db.close();
          return;
        }
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // A database left without its store (an interrupted creation) never
          // upgrades again at the same version: bump it once to add the store.
          const next = db.version + 1;
          db.close();
          if (!repair) {
            reject(new Error('IndexedDB store missing'));
            return;
          }
          openVersion(next, false).then(resolve, reject);
          return;
        }
        resolve(db);
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => {
        blocked = true;
        reject(new Error('IndexedDB open blocked'));
      };
    });

  const open = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    // No fixed version: a database already bumped by the repair above opens as is.
    const opening = openVersion(undefined, true).then((db) => {
      // Another tab upgrading the schema must not stay blocked by this one.
      db.onversionchange = () => {
        db.close();
        if (dbPromise === opening) dbPromise = null;
      };
      return db;
    });
    dbPromise = opening;
    // A failed open is retried at the next call rather than cached for the session.
    opening.catch(() => {
      if (dbPromise === opening) dbPromise = null;
    });
    return opening;
  };

  return {
    async get(key) {
      const db = await open();
      const value = await requestResult(db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key));
      return typeof value === 'string' ? value : null;
    },
    async write(entries) {
      if (entries.length === 0) return;
      const db = await open();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      for (const [key, value] of entries) {
        if (value === null) store.delete(key);
        else store.put(value, key);
      }
      await transactionDone(tx);
    },
  };
}
